import { useId, type CSSProperties } from 'react';
import './EvidenceRoadmap.css';

export type EvidenceRoadmapStatus = 'answered' | 'in-progress' | 'planned' | 'blocked' | 'unavailable';
export type EvidenceRoadmapConfidenceLevel = 'high' | 'medium' | 'low' | 'not-assessed';

export interface EvidenceRoadmapCoverage {
  /** Exact, human-readable coverage statement supplied by the evidence producer. */
  label: string;
  /** Optional numeric coverage. Both values are required when a coverage meter is desired. */
  observed?: number;
  total?: number;
  /** Optional source-defined observation window. */
  window?: string;
}

export interface EvidenceRoadmapBlockRange {
  /** Exact source-defined range label, including why a range is unavailable when applicable. */
  label: string;
  from: number | null;
  to: number | null;
}

export interface EvidenceRoadmapConfidence {
  level: EvidenceRoadmapConfidenceLevel;
  /** Source-supplied explanation of how the confidence assessment was reached. */
  rationale: string;
}

export interface EvidenceRoadmapSource {
  id: string;
  label: string;
  href?: string;
  note?: string;
}

export interface EvidenceRoadmapItem {
  id: string;
  question: string;
  domain: string;
  status: {
    state: EvidenceRoadmapStatus;
    /** Exact display label supplied by the evidence producer. */
    label: string;
    note?: string;
  };
  coverage: EvidenceRoadmapCoverage;
  answer: string;
  blockRange: EvidenceRoadmapBlockRange;
  method: string;
  confidence: EvidenceRoadmapConfidence;
  remainingLimitations: readonly [string, ...string[]];
  sources: readonly [EvidenceRoadmapSource, ...EvidenceRoadmapSource[]];
}

export interface EvidenceRoadmapProps {
  items: readonly EvidenceRoadmapItem[];
  title?: string;
  eyebrow?: string;
  description?: string;
  /** A source-supplied empty-state message. No default evidence claim is generated. */
  emptyMessage?: string;
  className?: string;
}

const blockFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function displayBlockRange(range: EvidenceRoadmapBlockRange) {
  if (range.from === null || range.to === null) return range.label;
  return `${blockFormatter.format(range.from)} → ${blockFormatter.format(range.to)} · ${range.label}`;
}

function coveragePercent(coverage: EvidenceRoadmapCoverage) {
  if (!Number.isFinite(coverage.observed) || !Number.isFinite(coverage.total) || Number(coverage.total) <= 0) return null;
  return Math.max(0, Math.min(100, (Number(coverage.observed) / Number(coverage.total)) * 100));
}

function Coverage({ coverage, itemId }: { coverage: EvidenceRoadmapCoverage; itemId: string }) {
  const percent = coveragePercent(coverage);
  const meterStyle = percent === null ? undefined : ({ '--er-coverage': `${percent}%` } as CSSProperties);

  return <div className="evidence-roadmap__coverage">
    <div>
      <span>COVERAGE</span>
      <strong id={`${itemId}-coverage`}>{coverage.label}</strong>
      {coverage.window ? <small>{coverage.window}</small> : null}
    </div>
    {percent === null ? null : <div
      className="evidence-roadmap__meter"
      role="progressbar"
      aria-labelledby={`${itemId}-coverage`}
      aria-valuemin={0}
      aria-valuemax={coverage.total}
      aria-valuenow={coverage.observed}
      style={meterStyle}
    ><i aria-hidden="true" /></div>}
  </div>;
}

function Source({ source }: { source: EvidenceRoadmapSource }) {
  const content = <>
    <span>{source.id}</span>
    <strong>{source.label}</strong>
    {source.note ? <small>{source.note}</small> : null}
  </>;

  return source.href
    ? <a className="evidence-roadmap__source" href={source.href} target="_blank" rel="noreferrer">{content}<b aria-hidden="true">↗</b><span className="evidence-roadmap__sr-only"> (opens in a new tab)</span></a>
    : <div className="evidence-roadmap__source">{content}</div>;
}

export function EvidenceRoadmap({
  items,
  title = 'Evidence roadmap',
  eyebrow = 'OPEN QUESTIONS / QUERY QUEUE',
  description,
  emptyMessage,
  className = '',
}: EvidenceRoadmapProps) {
  const classes = ['evidence-roadmap', className].filter(Boolean).join(' ');
  const instanceId = useId().replaceAll(':', '');
  const titleId = `${instanceId}-title`;

  return <section className={classes} aria-labelledby={titleId}>
    <header className="evidence-roadmap__head">
      <div>
        <p>{eyebrow}</p>
        <h3 id={titleId}>{title}</h3>
      </div>
      {description ? <p>{description}</p> : null}
      <span aria-label={`${items.length} evidence ${items.length === 1 ? 'question' : 'questions'}`}>
        <b>{String(items.length).padStart(2, '0')}</b>
        <small>QUESTIONS</small>
      </span>
    </header>

    {items.length === 0
      ? emptyMessage ? <p className="evidence-roadmap__empty" role="status">{emptyMessage}</p> : null
      : <ol className="evidence-roadmap__list">
        {items.map((item, index) => {
          const safeItemId = item.id.replace(/[^a-zA-Z0-9_-]/g, '-');
          const itemId = `${instanceId}-${safeItemId}-${index}`;
          const itemTitleId = `${itemId}-title`;
          const descriptionId = `${itemId}-answer`;
          return <li key={item.id}>
            <article
              className="evidence-roadmap__card"
              data-status={item.status.state}
              aria-labelledby={itemTitleId}
              aria-describedby={descriptionId}
            >
              <header className="evidence-roadmap__card-head">
                <span className="evidence-roadmap__index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <p>{item.domain}</p>
                  <h4 id={itemTitleId}>{item.question}</h4>
                </div>
                <div className="evidence-roadmap__status">
                  <i aria-hidden="true" />
                  <span>{item.status.label}</span>
                  {item.status.note ? <small>{item.status.note}</small> : null}
                </div>
              </header>

              <Coverage coverage={item.coverage} itemId={itemId} />

              <div className="evidence-roadmap__answer" id={descriptionId}>
                <span>CURRENT ANSWER</span>
                <p>{item.answer}</p>
              </div>

              <dl className="evidence-roadmap__method-grid">
                <div>
                  <dt>BLOCK RANGE</dt>
                  <dd>{displayBlockRange(item.blockRange)}</dd>
                </div>
                <div>
                  <dt>METHOD</dt>
                  <dd>{item.method}</dd>
                </div>
                <div data-confidence={item.confidence.level}>
                  <dt>CONFIDENCE</dt>
                  <dd><span>{item.confidence.level.replace('-', ' ').toUpperCase()}</span>{item.confidence.rationale}</dd>
                </div>
              </dl>

              <div className="evidence-roadmap__lower">
                <section aria-labelledby={`${itemId}-limitations`}>
                  <h5 id={`${itemId}-limitations`}>REMAINING LIMITATIONS</h5>
                  <ul>{item.remainingLimitations.map((limitation) => <li key={limitation}>{limitation}</li>)}</ul>
                </section>
                <section aria-labelledby={`${itemId}-sources`}>
                  <h5 id={`${itemId}-sources`}>SOURCES / RECEIPTS</h5>
                  <div>{item.sources.map((source) => <Source key={source.id} source={source} />)}</div>
                </section>
              </div>
            </article>
          </li>;
        })}
      </ol>}
  </section>;
}

export default EvidenceRoadmap;
